<template>
  <v-form class="mb-4" @submit.prevent="search">
    <v-row>
      <v-col cols="12" md="4">
        <v-text-field
          v-model="balanceName"
          label="Balance name"
          clearable
          hide-details
          density="compact"
        />
      </v-col>
      <v-col cols="12" md="4">
        <v-text-field
          v-model="accountNumber"
          label="Account number"
          clearable
          hide-details
          density="compact"
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-select
          v-model="balanceType"
          :items="balanceTypes"
          label="Balance type"
          clearable
          hide-details
          density="compact"
        />
      </v-col>
      <v-col cols="auto">
        <v-btn type="submit" :disabled="!!tableHelper.isLoading.value"
          >Search balances</v-btn
        >
      </v-col>
    </v-row>
  </v-form>
  <ReportingTableServer :table="tableHelper">
    <template #item.balanceType="{ item }">
      {{ upperSnakeToTitleCase(item.balanceType) }}
    </template>

    <template #item.accountNumber="{ item }">
      {{ item.balanceAccountNumber }}
    </template>

    <template #item.expirationDate="{ item }">
      {{ formatDateNY(item.balanceExpirationDate) }}
    </template>

    <template #item.amount="{ item }">
      {{ formatRawCost(item.currentAmountCents || 0) }}
    </template>

    <template #item.actions="{ item }">
      <v-btn
        v-if="pickMode"
        size="small"
        color="primary"
        :disabled="isPickDisabled(item)"
        @click="emits('select', item)"
        >Select</v-btn
      >
      <template v-else>
        <v-btn
          v-if="env.mode === 'STAFF'"
          icon="mdi-pencil"
          @click="editBalance(item)"
        />
        <StaffOrderDialog
          v-if="env.mode === 'STAFF'"
          title="Add Balance"
          :person-id="props.personId"
          :item-type="ItemType.GIFT_CARD"
          :restock-balance-type="item.balanceType"
          :balance-number="item.balanceAccountNumber ?? undefined"
          @complete="shopDone"
        />
        <ShopDialog
          v-else
          title="Add Balance"
          :view-max-width="1600"
          :item-type="ItemType.GIFT_CARD"
          :restock-balance-type="item.balanceType"
          :balance-number="item.balanceAccountNumber ?? undefined"
          @complete="shopDone"
        />
      </template>
    </template>

    <template
      v-slot:item.data-table-expand="{ internalItem, isExpanded, toggleExpand }"
    >
      <v-btn
        :append-icon="
          isExpanded(internalItem) ? 'mdi-chevron-up' : 'mdi-chevron-down'
        "
        :text="isExpanded(internalItem) ? 'Collapse' : 'More info'"
        class="text-none"
        color="medium-emphasis"
        size="small"
        variant="text"
        width="105"
        slim
        @click="toggleExpand(internalItem)"
      ></v-btn>
    </template>
    <template v-slot:expanded-row="{ columns, item }">
      <tr>
        <td :colspan="columns.length" class="py-2">
          <v-sheet rounded="lg">
            <v-table density="compact">
              <tbody class="bg-surface-light">
                <tr>
                  <th>Transaction Type</th>
                  <th>Transaction Date</th>
                  <th>Transaction Amount</th>
                </tr>
              </tbody>

              <tbody>
                <tr
                  v-for="transaction in item.transactions || []"
                  :key="transaction.assignId"
                >
                  <td class="py-2">
                    {{
                      upperSnakeToTitleCase(transaction.balanceTransactionType)
                    }}
                  </td>
                  <td class="py-2">
                    {{ formatDateNY(transaction.transactionDateTime) }}
                  </td>
                  <td class="py-2">
                    {{ formatRawCost(transaction.transactionAmountCents) }}
                  </td>
                </tr>
              </tbody>
            </v-table>
          </v-sheet>
        </td>
      </tr>
    </template>
  </ReportingTableServer>
</template>

<script lang="ts" setup>
import { useUtility } from "@/composables/useUtility";
import { upperSnakeToTitleCase } from "@/api/util";
import { useEnv } from "@/composables/useEnv";
import { ItemType } from "../../../../cbi-generator/generated/dto/ItemType";
import { BalanceType } from "../../../../cbi-generator/generated/dto/BalanceType";
import { T_Pagination, useTableHelper } from "@/composables/useTableHelper";
import { useBalanceService } from "../../../../cbi-generator/frontend/composables/useBalanceFrontendService";
import type { BalanceMember } from "../../../../cbi-generator/generated/dto/BalanceMember";
import type { BalanceSearchOptions } from "../../../../cbi-generator/generated/dto/BalanceSearchOptions";
import type { BalanceStaff } from "../../../../cbi-generator/generated/dto/BalanceStaff";
import type { PaginationResult } from "../../../../cbi-generator/generated/dto/PaginationResult";
import {
  memberOrderSessionKey,
  type MemberOrderSession,
} from "@/composables/shop/useMemberOrder";

const { formatDateNY, formatRawCost } = useUtility();

const tableHeadersRaw = [
  { title: "Balance Type", key: "balanceType" },
  { title: "Balance Name", key: "balanceName" },
  { title: "ID", key: "assignId" },
  { title: "Account Number", key: "accountNumber" },
  { title: "Expiration Date", key: "expirationDate", sortable: false },
  { title: "Amount", key: "amount" },
  { title: "Actions", key: "actions", sortable: false },
];

const env = useEnv();

const tableHeaders = computed(() =>
  env.mode == "MEMBER"
    ? tableHeadersRaw.filter((a) => a.key != "assignId")
    : tableHeadersRaw,
);

const props = defineProps<{
  personId?: number;
  externalTrigger?: number;
  /** Replaces the row actions with a Select button that emits `select`. */
  pickMode?: boolean;
  /** Balance ids whose Select button is disabled (e.g. already chosen). */
  pickDisabledIds?: number[];
}>();
function isPickDisabled(balance: BalanceStaff | BalanceMember) {
  if (props.pickDisabledIds?.includes(balance.assignId)) return true;
  const expires = balance.balanceExpirationDate;
  return expires != null && new Date(expires).getTime() < Date.now();
}
const balanceName = ref<string | null>(null);
const accountNumber = ref<string | null>(null);
const balanceType = ref<BalanceType | null>(null);
const balanceTypes = Object.values(BalanceType).map((value) => ({
  title: upperSnakeToTitleCase(value),
  value,
}));
const appliedFilters = ref<
  Pick<BalanceSearchOptions, "balanceName" | "accountNumber" | "balanceType">
>({
  balanceName: null,
  accountNumber: null,
  balanceType: null,
});
async function loadBalances(
  pagination: T_Pagination,
): Promise<PaginationResult<BalanceStaff | BalanceMember>> {
  const options: BalanceSearchOptions = {
    ...appliedFilters.value,
    person: props.personId != null ? { personId: props.personId } : null,
    requireLoad: false,
    requirePurchase: false,
    nonZero: false,
    page: pagination.page,
    perPage: pagination.perPage,
  };
  return env.mode == "STAFF"
    ? useBalanceService().balancesStaff(options)
    : useBalanceService().memberBalancesMember(options);
}

const tableHelper = useTableHelper({
  loadItemsServerPaginated: (pagination) => loadBalances(pagination),
  id: "Balances",
  itemValue: "assignId",
  extraHeaders: tableHeaders.value,
  defaultPagination: { page: 1, perPage: 10 },
});
async function search() {
  if (tableHelper.isLoading.value) return;
  appliedFilters.value = {
    balanceName: balanceName.value?.trim() || null,
    accountNumber: accountNumber.value?.trim() || null,
    balanceType: balanceType.value,
  };
  if (tableHelper.expandedRows) tableHelper.expandedRows.value = [];
  if (tableHelper.pagination.value.page !== 1) {
    tableHelper.pagination.value = { ...tableHelper.pagination.value, page: 1 };
  } else {
    await tableHelper.reloadItems();
  }
}
const emits = defineEmits<{
  (e: "editBalance", balance: BalanceStaff): void;
  (e: "select", balance: BalanceStaff | BalanceMember): void;
}>();
const memberOrder = inject<MemberOrderSession | null>(
  memberOrderSessionKey,
  null,
);
watch(
  () => memberOrder?.isComplete.value,
  (complete, previous) => {
    if (complete && !previous) tableHelper.reloadItems();
  },
);

function shopDone() {
  tableHelper.reloadItems();
}

function editBalance(balance: BalanceStaff) {
  emits("editBalance", balance);
}

watch(
  () => props.externalTrigger,
  () => {
    tableHelper.reloadItems();
  },
);
</script>
